import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Label, CardBody, CardHeader, FormGroup } from 'reactstrap';
import Select from 'react-select';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import { Plus, X } from 'react-feather';
import webservice from '../../../Service/webservice';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs/Breadcrumbs';
import Swal from 'sweetalert2';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../Service/crypto';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import api from '../../../api';

const Addinsurance: React.FC = () => {
  const [formSubmit, setFormSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const navigate = useNavigate();

  const Username = localStorage.getItem('UserName') || '';

  const AddPayerData = async (params: any) => {
    const key = await keyPromise;
    const Refid = uuidv4();
    const paramsS = params.params;
    const paramsObj = { ...paramsS, Refid };
    const EncParams = await AesEncrypt(key, paramsObj);

    const apiResponse = await api.fetch('api/Insurance/add', {
      method: 'POST',
      body: JSON.stringify({ EncParams })
    });

    const encryptedData = await apiResponse.json();
    const decryptedData = await AesDecrypt(key, encryptedData);


    return decryptedData; // <-- returning decrypted data
  };


  interface FormValues {
    Payername: string;
    PayerID: any;
    PortalLink: string;
    SoftwareName: boolean;
    LogoUrl: string;
  }

  const defaultValues: FormValues = {
    Payername: '',
    PayerID: '',
    PortalLink: '',
    SoftwareName: false,
    LogoUrl: '',
  }

  const AddAuditLogData = async (data: any) => {
    const key = await keyPromise
    data.Userid = localStorage.id;
    const ID = data.Refid;
    const EncParams = await AesEncrypt(key, { Data: data, ID });
    const response = await api.fetch('api/Insurance/addaudit', {
      method: 'POST',
      body: JSON.stringify({ EncParams })
    });
    return await response.json();
  };

  const handleSubmit = async (data: any) => {

    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    const RequiredData: { [key: string]: any } = {
      Payername: data.Payername,
      PayerID: data.PayerID,
      PortalLink: data.PortalLink,
      SoftwareName: data.SoftwareName,
      LogoUrl: data.LogoUrl,
    };

    // Validate only required fields (Payername and PayerID)
    if (data.Payername && data.PayerID && data.Payername.length > 0 && data.PayerID.length > 0) {
      try {
        // Log audit data before submission
        const auditLogData = Object.entries(RequiredData)
          .filter(([field, value]) => value !== undefined && value !== '')
          .map(([field, value]) => ({
            field,
            value,
          }));
        const Data = { auditLogData, Username };
        
        if (logoFile) {
          const formData = new FormData();
          formData.append('file', logoFile);
          
          try {
            const logoResponse = await api.fetch('api/Insurance/uploadLogo', {
              method: 'POST',
              body: formData,
            });
            const logoEncryptedData = await logoResponse.json();
            const logoDecryptedData = await AesDecrypt(await keyPromise, logoEncryptedData);
            const logoResult = typeof logoDecryptedData === 'string' ? JSON.parse(logoDecryptedData) : logoDecryptedData;
            if (logoResult.success) {
              RequiredData.LogoUrl = logoResult.LogoUrl;
            } else {
              Swal.fire({
                title: 'Upload Error',
                icon: 'error',
                text: 'Failed to upload logo: ' + (logoResult.error || logoResult.message || 'Unknown error'),
              });
              setIsSubmitting(false);
              return;
            }
          } catch (e) {
             console.error("Exception during upload: ", e);
             Swal.fire({
                title: 'Error',
                icon: 'error',
                text: 'System error during upload. Check console.',
              });
              setIsSubmitting(false);
              return;
          }
        }

        const refResponse = await AddPayerData({ params: RequiredData });
        const refData = refResponse;
        const Refid = refData.success ? refData.Refid : refData.message;

        // Add audit log entry after successful submission
        await AddAuditLogData({ ...Data, Refid, action: "Payer Mapping added" });

        Swal.fire({
          title: 'Payer Mapping Management',
          icon: refData.success ? 'success' : 'error',
          text: refData.success ? 'Payer Mapping Added Successfully!' : Refid,
          allowOutsideClick: false,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        });
        setIsSubmitting(false);
        if (refData.success) {
          navigate(`/insurance`);
        }
      } catch (error) {
        console.error('Error adding Payer Mapping:', error);
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };
  const validationSchema = Yup.object().shape({

    Payername: Yup.string().required('Payer Name is required'),
    // Email: Yup.string().email('Invalid email address').required('Email is required'),
    PayerID: Yup.string().required('PayerID is required'),
  });
  const handleNumbers = (event: any) => {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  }

  return (
    <>
      <Breadcrumbs mainTitle='Insurance  Management' parent='Insurance  Management' title='Insurance  Management' to='/insurance' />
      <Fragment>
        <Card>
          <CardHeader style={{ backgroundColor: '#B4B4B3' }}>
            <h3>Add Payer</h3>
          </CardHeader>
          <CardBody className='py-2 my-25'>
            <Formik
              initialValues={defaultValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                handleSubmit(values);
                setFormSubmit(true);
              }}
            >
              {({ values }) => (
                <Form className={`needs-validation custom-input ${formSubmit ? 'was-validated' : ''}`} noValidate>
                  <Row className="g-3">
                    <Col md={4} xs={12}>
                      <Label>Payer ID<span style={{ color: 'red' }}>*</span></Label>
                      <Field name="PayerID" type="text" className="form-control" placeholder="" required />
                      <ErrorMessage name="PayerID" component="span" className="text-danger" />
                    </Col>
                    <Col md={4} xs={12}>
                      <Label>Payer Name<span style={{ color: 'red' }}>*</span></Label>
                      <Field name="Payername" type="text" className="form-control" placeholder="" required />
                      <ErrorMessage name="Payername" component="span" className="text-danger" />
                    </Col>
                    <Col md={4} xs={12}>
                      <Label>Portal Link</Label>
                      <Field name="PortalLink" as="textarea" className="form-control" placeholder="" rows={3} />
                      <ErrorMessage name="PortalLink" component="span" className="text-danger" />
                    </Col>
                    <Col md={4} xs={12}>
                      <FormGroup>
                        <Label check>Software Name Required</Label>
                        <div className="form-check form-switch form-check-success" style={{ marginTop: '8px' }}>
                          <Field name="SoftwareName">
                            {({ field, form }: any) => (
                              <input
                                {...field}
                                type="checkbox"
                                className="form-check-input"
                                role="switch"
                                id="softwareNameSwitch"
                                checked={field.value}
                                onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                              />
                            )}
                          </Field>
                          <Label check for="softwareNameSwitch">{values.SoftwareName ? 'Enabled' : 'Disabled'}</Label>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={4} xs={12}>
                      <Label>Payer Logo</Label>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="form-control" 
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            setLogoFile(e.target.files[0]);
                          }
                        }} 
                      />
                    </Col>
                    <Col>
                      <div className="text-end" style={{ marginTop: "100px" }}>
                        <button type="submit" className="btn btn-success me-3">Submit</button>
                        <Link to='/insurance'>
                          <button type="button" className="btn btn-danger">Cancel</button>
                        </Link>
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </Fragment>
    </>
  );
};

export default Addinsurance;
